/**
 * Pure helpers for the Business Settings timezone picker.
 *
 * Builds a searchable list of IANA timezones with friendly labels such as
 * "Asia/Kolkata (GMT+5:30)". The browser-detected zone and the currently-selected
 * zone are pinned to the top of the list; "UTC" is always present.
 *
 * Reference: mirrors the display-timezone pattern in
 * `apps/portal-web/src/routes/public/[slug]/[sessionSlug]/schedulerTimezone.ts`
 * (read-only reference — not imported across apps).
 */

/** Curated fallback list used when `Intl.supportedValuesOf('timeZone')` is unavailable. */
export const FALLBACK_TIMEZONES: readonly string[] = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
  'Australia/Lord_Howe',
  'Pacific/Auckland',
  'Pacific/Honolulu',
];

/** Option shape consumed by `Combobox01` (`{ value, label }`). */
export interface TimezoneOption {
  /** IANA timezone identifier (stored on the business). */
  value: string;
  /** Friendly label, e.g. "Asia/Kolkata (GMT+5:30)". */
  label: string;
}

/** Detect the viewer's browser timezone via Intl; fall back to 'UTC'. */
export function detectBrowserTimezone(): string {
  if (typeof window === 'undefined' || typeof Intl === 'undefined') {
    return 'UTC';
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.length > 0 ? tz : 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * True when `raw` is a non-empty IANA id (`America/Los_Angeles`, `UTC`, `GMT`).
 * Rejects offsets and abbreviations so a stored `UTC` is not treated as missing.
 */
export function isValidIanaTimezone(raw: unknown): raw is string {
  if (typeof raw !== 'string') return false;
  const t = raw.trim();
  if (!t) return false;
  if (/^[+-]\d/.test(t)) return false;
  if (!t.includes('/')) {
    const upper = t.toUpperCase();
    if (upper !== 'UTC' && upper !== 'GMT') return false;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: t });
    return true;
  } catch {
    return false;
  }
}

/**
 * Form prefill only. Keeps a valid stored IANA id, including `UTC`.
 * Missing, blank, or invalid values use the browser zone so the next Save can persist it.
 * Does not write to the API.
 */
export function resolveBusinessSettingsFormTimezone(
  stored: unknown,
  detectBrowser: () => string = detectBrowserTimezone
): string {
  if (isValidIanaTimezone(stored)) {
    return stored.trim();
  }
  return detectBrowser();
}

/** Broad IANA list, preferring the runtime's supported values. */
export function getTimezoneList(): string[] {
  try {
    const supported = (
      Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
      }
    ).supportedValuesOf?.('timeZone');
    if (supported && supported.length > 0) {
      return supported;
    }
  } catch {
    // supportedValuesOf not available — fall through to curated list.
  }
  return [...FALLBACK_TIMEZONES];
}

/**
 * Derive a short GMT offset for an IANA zone, e.g. "GMT+5:30" or "GMT-5".
 * Returns "GMT" for UTC and when Intl cannot describe the zone.
 *
 * Normalizes Node's `longOffset` output (e.g. "GMT+05:30" → "GMT+5:30",
 * "GMT+09:00" → "GMT+9") so labels match the "Asia/Kolkata (GMT+5:30)" form.
 */
export function formatTimezoneOffset(iana: string): string {
  const raw = resolveRawOffset(iana);
  if (!raw) return 'GMT';
  return normalizeGmtOffset(raw);
}

function resolveRawOffset(iana: string): string | null {
  for (const style of ['longOffset', 'shortOffset'] as const) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: iana,
        timeZoneName: style,
      }).formatToParts(new Date());
      const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
      if (offset) return offset;
    } catch {
      // try next style
    }
  }
  return null;
}

function normalizeGmtOffset(offset: string): string {
  // Node/ICU may emit GMT+0 / GMT-0 / GMT+00:00 for UTC — normalize to "GMT".
  if (/^GMT([+-]0|:00|:00:00)?$/i.test(offset) || /^GMT[+-]0{1,2}(:00)?$/i.test(offset)) {
    return 'GMT';
  }
  // Matches "GMT+05:30", "GMT-09:00", "GMT+00:00", etc.
  const match = offset.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!match) return offset;
  const [, sign, hour, minute] = match;
  const h = Number.parseInt(hour, 10);
  if (h === 0 && minute === '00') return 'GMT';
  if (minute === '00') return `GMT${sign}${h}`;
  return `GMT${sign}${h}:${minute}`;
}

/**
 * Build a friendly label for an IANA zone, e.g. "Asia/Kolkata (GMT+5:30)".
 * Falls back to the raw IANA identifier when Intl cannot describe the zone.
 */
export function formatTimezoneLabel(iana: string): string {
  if (!iana) return 'UTC (GMT)';
  const offset = formatTimezoneOffset(iana);
  return `${iana} (${offset})`;
}

/**
 * Build the combobox option list for the business timezone picker.
 *
 * Order:
 *   1. Currently-selected zone (if any)
 *   2. Browser-detected zone (if distinct)
 *   3. All supported / curated zones (UTC always included), de-duplicated
 *
 * Each option's `value` is the IANA identifier (what gets stored); `label` is
 * the friendly "IANA (GMT±x)" string shown in the trigger and dropdown.
 */
export function buildTimezoneOptions(selected: string): TimezoneOption[] {
  const browserTz = detectBrowserTimezone();
  const pinned: string[] = [];
  const seen = new Set<string>();

  const pushIfNew = (tz: string) => {
    if (!tz) return;
    if (seen.has(tz)) return;
    seen.add(tz);
    pinned.push(tz);
  };

  pushIfNew(selected);
  pushIfNew(browserTz);

  const rest: string[] = [];
  for (const tz of getTimezoneList()) {
    if (!seen.has(tz)) {
      seen.add(tz);
      rest.push(tz);
    }
  }
  // Guarantee UTC is always present even if the runtime list omits it.
  if (!seen.has('UTC')) {
    rest.unshift('UTC');
  }

  return [...pinned, ...rest].map((tz) => ({
    value: tz,
    label: formatTimezoneLabel(tz),
  }));
}
